import React, { useState } from 'react';
import { Info, Edit, X, Save, AlertTriangle } from 'lucide-react';
import { useProject } from '../contexts/ProjectContext';
import toast from 'react-hot-toast';

const ProjectContextDisplay: React.FC = () => {
  const { currentProject, updateProject } = useProject();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    project_context: currentProject?.project_context || '',
    analysis_instructions: currentProject?.analysis_instructions || ''
  });

  if (!currentProject) return null;

  const hasContext = currentProject.project_context || currentProject.analysis_instructions;

  const handleSave = async () => {
    try {
      await updateProject(currentProject.id, editData);
      setIsEditing(false);
      toast.success('Project context updated successfully');
    } catch (error) {
      toast.error('Failed to update project context');
    }
  };

  const handleCancel = () => {
    setEditData({
      project_context: currentProject.project_context || '',
      analysis_instructions: currentProject.analysis_instructions || ''
    });
    setIsEditing(false);
  };

  // Hide the "Context Not Set" warning box for now - will be used in future
  if (!hasContext && !isEditing) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Info className="w-5 h-5 text-blue-600" />
          <span className="text-blue-800 font-semibold">{currentProject.name} Context & Analysis Focus</span>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
          >
            <Edit className="w-4 h-4" />
            <span className="text-sm">Edit</span>
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Context
            </label>
            <textarea
              value={editData.project_context}
              onChange={(e) => setEditData({ ...editData, project_context: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Describe what this project is about, the scope, and key objectives..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Analysis Instructions
            </label>
            <textarea
              value={editData.analysis_instructions}
              onChange={(e) => setEditData({ ...editData, analysis_instructions: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Provide specific instructions for AI analysis focus..."
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={handleCancel}
              className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800"
            >
              <X className="w-4 h-4" />
              <span>Cancel</span>
            </button>
            <button
              onClick={handleSave}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Save className="w-4 h-4" />
              <span>Save</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {currentProject.project_context && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Project Context</h4>
              <p className="text-gray-800 bg-white p-3 rounded-lg border">
                {currentProject.project_context}
              </p>
            </div>
          )}

          {currentProject.analysis_instructions && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 text-orange-500" />
                <span>Analysis Focus Instructions</span>
              </h4>
              <p className="text-gray-800 bg-orange-50 p-3 rounded-lg border border-orange-200">
                {currentProject.analysis_instructions}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectContextDisplay; 